import React, { useState, useEffect, useMemo } from "react";
import { addHardwareBackListener } from "@/lib/platform";
import { base44 } from "@/api/base44Client";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import AppHeader from "@/components/drill/AppHeader";
import DrillRunForm from "@/components/drill/DrillRunForm";
import DrillRunRow from "@/components/drill/DrillRunRow";
import ChipSectionForm from "@/components/drill/ChipSectionForm";
import HoleForm from "@/components/drill/HoleForm";
import ChipSectionRow from "@/components/drill/ChipSectionRow";
import SignatureSection from "@/components/drill/SignatureSection";
import DeletedRunRestoreBar from "@/components/drill/DeletedRunRestoreBar";
import DepthAlertSplash from "@/components/drill/DepthAlertSplash";
import AlertsListDialog from "@/components/drill/AlertsListDialog";
import {
  backupDeletedHole,
  backupDeletedRun,
  listDeletedRuns,
  removeDeletedRun,
} from "@/lib/deletedBackup";
import PullToRefresh from "@/components/PullToRefresh";
import { exportRunsToCSV, exportRunsToPDF } from "@/components/drill/exportRuns";
import { Plus, Download, FileSpreadsheet, FileText, Trash2, Pencil, Check, X, AlertTriangle, Settings, ChevronDown, PackageOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import MobileSelect from "@/components/drill/MobileSelect";
import StatusBadge from "@/components/drill/StatusBadge";
import { isAtLeast } from "@/lib/roles";
import { getTotalDepth } from "@/lib/depthCalculations";
import { useAuth } from "@/lib/AuthContext";
import { useFeaturePermissions } from "@/lib/FeaturePermissionContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Disc3,  Bell, AlertOctagon } from "lucide-react";

const STATUS_OPTIONS = ["Active", "Completed", "On Hold"].map((s) => ({ value: s, label: s }));

// A run stays "in progress" until it has BOTH a to_depth and a recovered amount.
const isRunInProgress = (r) =>
  r.record_type === "chip_section" ? r.to_depth == null : (r.to_depth == null || r.recovered == null);

// Sort order: in-progress runs first, then completed (incl. chip sections) by
// depth descending.
const sortRuns = (a, b) => {
  const aIP = isRunInProgress(a);
  const bIP = isRunInProgress(b);
  if (aIP && !bIP) return -1;
  if (!aIP && bIP) return 1;
  const av = Number(a.to_depth ?? a.from_depth) || 0;
  const bv = Number(b.to_depth ?? b.from_depth) || 0;
  return bv - av;
};

// Total Depth is calculated by the run sheet; all consumers use this
// authoritative value, including legacy split Conventional Core records.
const getEffectiveRunToDepth = (run, holeType, rodLength = 6) =>
  getTotalDepth(run, holeType, rodLength);

export default function RunSheet() {
  const { effectiveRole } = useAuth();
  const { canUseFeature, loading: permissionsLoading } = useFeaturePermissions();
  const { sheetId } = useParams();
  const navigate = useNavigate();
  const [sheet, setSheet] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [editingRun, setEditingRun] = useState(null);
  const [editingChip, setEditingChip] = useState(null);

  const runDialogOpen = searchParams.has("editRun");
  const chipDialogOpen = searchParams.has("editChip") || searchParams.has("newChip");

  // Clear editing state when dialogs close via back gesture (URL param removed)
  useEffect(() => {
    if (!runDialogOpen) setEditingRun(null);
  }, [runDialogOpen]);

  useEffect(() => {
    if (!chipDialogOpen) setEditingChip(null);
  }, [chipDialogOpen]);

  const openRunDialog = (run) => {
    setEditingRun(run);
    const next = new URLSearchParams(searchParams);
    next.set("editRun", run.id);
    setSearchParams(next);
  };

  const closeRunDialog = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("editRun");
    setSearchParams(next);
  };

  const openChipDialog = (chip) => {
    setEditingChip(chip);
    const next = new URLSearchParams(searchParams);
    if (chip) next.set("editChip", chip.id);
    else next.set("newChip", "1");
    setSearchParams(next);
  };

  const closeChipDialog = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("editChip");
    next.delete("newChip");
    setSearchParams(next);
  };
  const [exporting, setExporting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingHole, setEditingHole] = useState(false);
  const [holeNameDraft, setHoleNameDraft] = useState("");
  const [editHoleOpen, setEditHoleOpen] = useState(false);
  const [savingHole, setSavingHole] = useState(false);
  const [deletedRuns, setDeletedRuns] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [alertsListOpen, setAlertsListOpen] = useState(false);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [casingWarning, setCasingWarning] = useState(null);

  const rerankAndSet = (data) => {
    const asc = [...data].sort((a, b) => (Number(a.from_depth) || 0) - (Number(b.from_depth) || 0));
    // Number only "run" records sequentially by depth order; chip sections stay unnumbered.
    let runIdx = 0;
    const posById = {};
    const updates = [];
    asc.forEach((r) => {
      if (r.record_type === "chip_section") return;
      runIdx += 1;
      posById[r.id] = runIdx;
      if ((r.run_number || 0) !== runIdx) updates.push({ id: r.id, run_number: runIdx });
    });
    if (updates.length) {
      base44.entities.DrillRun.bulkUpdate(updates).catch((e) => console.error(e));
    }
    const normalized = data
      .map((r) => ({ ...r, run_number: r.record_type === "chip_section" ? r.run_number : (posById[r.id] ?? r.run_number) }))
      .sort((a, b) => {
        const aDepth = getEffectiveRunToDepth(a, sheet?.hole_type, sheet?.run_size) ?? (Number(a.to_depth ?? a.from_depth) || 0);
        const bDepth = getEffectiveRunToDepth(b, sheet?.hole_type, sheet?.run_size) ?? (Number(b.to_depth ?? b.from_depth) || 0);
        return bDepth - aDepth;
      });
    setRuns(normalized);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, data] = await Promise.all([
        base44.entities.Sheet.get(sheetId),
        base44.entities.DrillRun.filter({ sheet_id: sheetId }, "-to_depth"),
      ]);
      setSheet(s);
      rerankAndSet(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const loadAlerts = async () => {
    try {
      const list = await base44.entities.DepthAlert.filter({ sheet_id: sheetId });
      setAlerts(list || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (permissionsLoading || !sheet) return;
    const type = String(sheet.hole_type || "Diamond").toLowerCase();
    const feature = type === "conventional" ? "drilling_conventional" : type === "chipping" ? "drilling_chipping" : "drilling_diamond";
    if (!canUseFeature(feature)) navigate("/dashboard", { replace: true });
  }, [sheet, permissionsLoading, canUseFeature, navigate]);

  useEffect(() => {
    load();
    loadAlerts();
    setDeletedRuns(listDeletedRuns().filter((e) => e.run?.sheet_id === sheetId));
  }, [sheetId]);

  // Handle Android hardware Back explicitly so the WebView never exits while
  // the user is inside a Run Sheet. A dialog gets first priority; otherwise
  // Back returns to the run-card/dashboard page.
  useEffect(() => {
    const handleAndroidBack = async () => {
      if (runDialogOpen) {
        closeRunDialog();
        return;
      }
      if (chipDialogOpen) {
        closeChipDialog();
        return;
      }
      navigate("/dashboard", { replace: true });
    };

    let listener;
    addHardwareBackListener(handleAndroidBack).then((handle) => {
      listener = handle;
    });

    return () => {
      listener?.remove();
    };
  }, [runDialogOpen, chipDialogOpen, navigate]);

  // Also support browser/WebView history Back where popstate is available.
  useEffect(() => {
    const handlePopState = () => {
      if (runDialogOpen) {
        closeRunDialog();
        return;
      }
      if (chipDialogOpen) {
        closeChipDialog();
        return;
      }
      navigate("/dashboard", { replace: true });
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [runDialogOpen, chipDialogOpen, navigate]);


  const runTypeRuns = useMemo(() => runs.filter((r) => (r.record_type || "run") === "run"), [runs]);

  const nextRunNumber = useMemo(() => runTypeRuns.length + 1, [runTypeRuns]);

  const runNumberMap = useMemo(() => {
    const asc = [...runTypeRuns].sort((a, b) => (Number(a.from_depth) || 0) - (Number(b.from_depth) || 0));
    const map = {};
    asc.forEach((r, i) => { map[r.id] = i + 1; });
    return map;
  }, [runTypeRuns]);

  const deepestCompleted = useMemo(() => {
    const completed = runTypeRuns.filter((r) => r.to_depth != null);
    return completed.reduce((deepest, r) => {
      if (!deepest) return r;
      const d = getEffectiveRunToDepth(r, sheet?.hole_type, sheet?.run_size) ?? 0;
      const dd = getEffectiveRunToDepth(deepest, sheet?.hole_type, sheet?.run_size) ?? 0;
      return d >= dd ? r : deepest;
    }, null);
  }, [runTypeRuns, sheet?.hole_type, sheet?.run_size]);

  // For a split Conventional Core run, the second physical rod determines
  // the final stick-up carried into the next consecutive Core run.
  const previousRunEndStickUp = useMemo(() => {
    if (!deepestCompleted) return null;
    const splitEnd = Number(deepestCompleted.rod2_end_stick_up);
    if (Number.isFinite(splitEnd)) return splitEnd;
    const end = Number(deepestCompleted.end_stick_up);
    return Number.isFinite(end) ? end : null;
  }, [deepestCompleted]);

  // Most recently entered Total Rod Count, regardless of drilling method.
  const latestTotalRodCount = useMemo(() => {
    const withCount = runs.filter((r) => r.total_rod_count != null && r.total_rod_count !== "");
    if (!withCount.length) return null;
    const sorted = [...withCount].sort((a, b) => {
      const ad = getEffectiveRunToDepth(a, sheet?.hole_type, sheet?.run_size) ?? (Number(a.from_depth) || 0);
      const bd = getEffectiveRunToDepth(b, sheet?.hole_type, sheet?.run_size) ?? (Number(b.from_depth) || 0);
      if (bd !== ad) return bd - ad;
      return String(b.updated_date || b.created_date || "").localeCompare(String(a.updated_date || a.created_date || ""));
    });
    return sorted[0]?.total_rod_count ?? null;
  }, [runs]);

  // Core depth = greatest run depth; Chip depth = greatest chip-section depth.
  // Current depth is whichever of the two is greater.
  const coreDepth = useMemo(
    () =>
      runTypeRuns.reduce(
        (m, r) => Math.max(
          m,
          Number(getEffectiveRunToDepth(r, sheet?.hole_type, sheet?.run_size) ?? r.from_depth) || 0
        ),
        0
      ),
    [runTypeRuns, sheet?.hole_type, sheet?.run_size]
  );
  const chipDepth = useMemo(
    () =>
      runs
        .filter((r) => r.record_type === "chip_section")
        .reduce(
          (m, r) => Math.max(m, Number(r.to_depth ?? r.from_depth) || 0),
          0
        ),
    [runs]
  );

  const latestChipDepth = useMemo(() => {
    const chips = runs.filter((r) => r.record_type === "chip_section" && r.to_depth != null);
    if (!chips.length) return null;
    return chips.reduce((latest, r) => {
      const d = Number(r.to_depth) || 0;
      return d > (Number(latest) || 0) ? d : latest;
    }, 0);
  }, [runs]);

  const latestChip = useMemo(() => {
    const chips = runs.filter((r) => r.record_type === "chip_section");
    return chips.reduce((latest, r) => {
      if (!latest) return r;
      return (Number(r.to_depth) || 0) >= (Number(latest.to_depth) || 0) ? r : latest;
    }, null);
  }, [runs]);

  // When a Conventional Core run is followed immediately by a Chip section,
  // the Chip must start from the Core run's actual total depth. A split Core
  // run stores the first rod's depth in to_depth, so include the second rod's
  // drilled metres when determining that starting depth.
  const latestCompletedRecord = useMemo(() => {
    const completed = runs.filter((r) => r.to_depth != null);
    return completed.reduce((latest, r) => {
      if (!latest) return r;
      const d = r.record_type === "chip_section"
        ? (Number(r.to_depth) || 0)
        : (getEffectiveRunToDepth(r, sheet?.hole_type, sheet?.run_size) || 0);
      const ld = latest.record_type === "chip_section"
        ? (Number(latest.to_depth) || 0)
        : (getEffectiveRunToDepth(latest, sheet?.hole_type, sheet?.run_size) || 0);
      return d >= ld ? r : latest;
    }, null);
  }, [runs, sheet?.hole_type, sheet?.run_size]);

  const latestCoreTotalDepth = useMemo(() => {
    if (sheet?.hole_type !== "Conventional" || latestCompletedRecord?.record_type !== "run") return null;
    return getEffectiveRunToDepth(latestCompletedRecord, sheet?.hole_type, sheet?.run_size);
  }, [latestCompletedRecord, sheet?.hole_type, sheet?.run_size]);

  const currentDepth = useMemo(
    () => Math.max(coreDepth, chipDepth),
    [coreDepth, chipDepth]
  );

  const nextChipRodNumber = useMemo(() => {
    const latestCoreDepth = Number(latestCoreTotalDepth);
    const latestChipD = Number(latestChipDepth);
    const coreIsMostRecent = Number.isFinite(latestCoreDepth) && latestCoreDepth > latestChipD + 0.001;

    // If switching directly from Conventional Core to Chip, derive the
    // physical rod number from the Core's authoritative total depth. This
    // prevents the Chip Total Depth from being calculated below its From Depth.
    if (coreIsMostRecent && sheet?.hole_type === "Conventional") {
      const length = Number(sheet?.run_size || 6);
      const bha = Number(sheet?.bha_length || 0);
      const table = Number(sheet?.chip_table_height ?? sheet?.table_height ?? 0);
      const depth = latestCoreDepth;
      const required = depth - (bha - table);
      return Math.max(1, Number.isFinite(length) && length > 0 ? Math.ceil(required / length) : 1);
    }

    const n = Number(latestChip?.rod_number);
    if (!Number.isFinite(n)) {
      const length = Number(sheet?.run_size || 6);
      const bha = Number(sheet?.bha_length || 0);
      const table = Number(sheet?.chip_table_height ?? sheet?.table_height ?? 0);
      const depth = Number(currentDepth) || 0;
      const required = depth - (bha - table);
      return Math.max(1, Number.isFinite(length) && length > 0 ? Math.ceil(required / length) : 1);
    }
    return latestChip?.rod_not_completed ? n : n + 1;
  }, [latestChip, latestChipDepth, latestCoreTotalDepth, sheet?.hole_type, sheet?.run_size, sheet?.bha_length, sheet?.chip_table_height, sheet?.table_height, currentDepth]);

  const nextChipFinishesIncomplete = !!latestChip?.rod_not_completed;

  // Conventional-only rod requirement. Chip uses BHA-table height; Core uses
  // barrel-table height. Other drilling methods are intentionally excluded.
  const conventionalMethod = sheet?.conventional_method === "Chip" ? "Chip" : "Core";
  const rodRequirement = useMemo(() => {
    if (sheet?.hole_type !== "Conventional") return null;
    const isChip = conventionalMethod === "Chip";
    const rodLength = Number(sheet?.run_size || 6);
    // Always use the currently active BHA/barrel. The hole-creation
    // BHA/barrel is retained as historical configuration, but must not
    // drive the live "rods required to touch bottom" calculation after
    // the active BHA/barrel has been changed.
    const offset = isChip
      ? (Number.isFinite(Number(sheet?.bha_length))
          ? Number(sheet.bha_length) - Number(sheet?.chip_table_height ?? sheet?.table_height ?? 0)
          : Number(sheet?.bha_table_height))
      : (Number.isFinite(Number(sheet?.barrel_length))
          ? Number(sheet.barrel_length) - Number(sheet?.core_table_height ?? sheet?.table_height ?? 0)
          : Number(sheet?.barrel_table_height));
    const depth = Number(currentDepth);
    if (!Number.isFinite(rodLength) || rodLength <= 0 || !Number.isFinite(offset) || !Number.isFinite(depth)) return null;
    const requiredLength = Math.max(0, depth - offset);
    const rods = requiredLength <= 0 ? 0 : Math.ceil(requiredLength / rodLength);
    const stickUp = rods > 0 ? +(rods * rodLength + offset - depth).toFixed(2) : 0;
    return { method: conventionalMethod, rods, stickUp, offset, rodLength };
  }, [sheet, currentDepth, conventionalMethod]);

  // Detect gaps between consecutive records (runs + chip sections) by depth.
  // IMPORTANT: a split Conventional Core run stores only Rod 1's end in
  // `to_depth`, so use its effective physical end including Rod 2 here.
  // Otherwise the Run Sheet incorrectly reports a gap between the end of Core
  // and the start of the following Chip section.
  const gaps = useMemo(() => {
    const sorted = [...runs].sort(
      (a, b) => (Number(a.from_depth) || 0) - (Number(b.from_depth) || 0)
    );
    const result = [];
    for (let i = 1; i < sorted.length; i++) {
      const prevTo = sorted[i - 1].record_type === "chip_section"
        ? Number(sorted[i - 1].to_depth)
        : getEffectiveRunToDepth(sorted[i - 1], sheet?.hole_type, sheet?.run_size);
      const currFrom = Number(sorted[i].from_depth);
      if (!Number.isFinite(prevTo) || !Number.isFinite(currFrom)) continue;
      // Only a positive gap is missing data. Overlaps and exact continuity
      // are valid; comparing absolute difference incorrectly flagged every
      // adjacent record when the cards were displayed newest-first.
      if (currFrom - prevTo > 0.001) {
        result.push({
          prevLabel: sorted[i - 1].record_type === "chip_section" ? "Chip" : `Run ${sorted[i - 1].run_number ?? ""}`,
          currLabel: sorted[i].record_type === "chip_section" ? "Chip" : `Run ${sorted[i].run_number ?? ""}`,
          expected: +prevTo.toFixed(2),
          actual: +currFrom.toFixed(2),
          currId: sorted[i].id,
        });
      }
    }
    return result;
  }, [runs, sheet?.hole_type, sheet?.run_size]);

  // Cards are displayed newest-first, so a real gap belongs immediately
  // before the deeper record's following lower-depth card. Use the same gap
  // calculation above rather than re-running a different absolute-difference
  // check in the renderer.
  const gapBeforeIds = useMemo(() => new Set(gaps.map((g) => g.currId).filter(Boolean)), [gaps]);

  const handleChangeMethod = async () => {
    if (!sheet || sheet.hole_type !== "Conventional") return;
    const nextMethod = conventionalMethod === "Chip" ? "Core" : "Chip";
    try {
      const updated = await base44.entities.Sheet.update(sheetId, { conventional_method: nextMethod });
      setSheet(updated);
      await load();
    } catch (e) {
      console.error(e);
    }
  };

  const handleNewRun = async () => {
    setNewMenuOpen(false);
    const from = currentDepth;
    const prevEnd = previousRunEndStickUp;
    const directlyAfterChip =
      sheet?.hole_type === "Conventional" &&
      conventionalMethod === "Core" &&
      latestChip &&
      Math.abs((Number(latestChip.to_depth) || 0) - (Number(currentDepth) || 0)) < 0.001;
    const startStickUp = directlyAfterChip && rodRequirement
      ? rodRequirement.stickUp
      : (prevEnd == null ? null : prevEnd === 0 ? (sheet?.run_size ?? 6) : prevEnd);
    const lastDriller = sheet?.drillers?.length ? sheet.drillers[sheet.drillers.length - 1] : undefined;
    try {
      const startTime = new Date().toTimeString().slice(0,5);
      const openRuns = runs.filter((r) => !r.end_time);
      if (openRuns.length) await base44.entities.DrillRun.bulkUpdate(openRuns.map((r) => ({ id: r.id, end_time: startTime })));
      const created = await base44.entities.DrillRun.create({
        record_type: "run",
        sheet_id: sheetId,
        hole_id: sheet?.name || "",
        run_number: nextRunNumber,
        from_depth: from,
        stick_up: startStickUp,
        start_time: startTime,
        driller: lastDriller,
        date: (() => {
          const d = new Date();
          const tz = d.getTimezoneOffset() * 60000;
          return new Date(d - tz).toISOString().split("T")[0];
        })(),
      });
      setEditingRun({ ...created, driller: created.driller || lastDriller });
      const next = new URLSearchParams(searchParams);
      next.set("editRun", created.id);
      setSearchParams(next);
      load();
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmit = async (data, { close } = {}) => {
    const prev = runs;
    if (editingRun) {
      setRuns((p) =>
        p
          .map((r) => (r.id === editingRun.id ? { ...r, ...data } : r))
          .sort(sortRuns)
      );
      try {
        await base44.entities.DrillRun.update(editingRun.id, data);
        if (close) closeRunDialog();
      } catch (e) {
        setRuns(prev);
        throw e;
      }
      load();
    } else {
      const optimistic = {
        ...data,
        id: `temp-${Date.now()}`,
        sheet_id: sheetId,
        hole_id: sheet?.name || "",
        created_date: new Date().toISOString(),
      };
      setRuns((p) => [optimistic, ...p].sort(sortRuns));
      try {
        await base44.entities.DrillRun.create({
          ...data,
          record_type: "run",
          sheet_id: sheetId,
          hole_id: sheet?.name || "",
        });
        if (close) closeRunDialog();
      } catch (e) {
        setRuns(prev);
        throw e;
      }
      load();
    }
  };

  const handleChipSubmit = async (data) => {
    const prev = runs;
    if (editingChip) {
      setRuns((p) =>
        p
          .map((r) => (r.id === editingChip.id ? { ...r, ...data } : r))
          .sort(sortRuns)
      );
      try {
        await base44.entities.DrillRun.update(editingChip.id, data);
        closeChipDialog();
      } catch (e) {
        setRuns(prev);
        throw e;
      }
      load();
      return;
    }

    const startTime = data.start_time || new Date().toTimeString().slice(0, 5);
    const openRecords = runs.filter((r) => !r.end_time);
    if (openRecords.length) {
      await base44.entities.DrillRun.bulkUpdate(
        openRecords.map((r) => ({ id: r.id, end_time: startTime }))
      );
    }

    const chipCount = runs.filter((r) => r.record_type === "chip_section").length;
    const payload = {
      ...data,
      start_time: startTime,
      record_type: "chip_section",
      sheet_id: sheetId,
      hole_id: sheet?.name || "",
      rod_number: data.rod_number ?? chipCount + 1,
      date: new Date().toISOString().split("T")[0],
      run_number: nextRunNumber,
    };
    const created = await base44.entities.DrillRun.create(payload);
    setRuns((p) => [
      { ...payload, ...created, id: created?.id || `temp-${Date.now()}` },
      ...p.filter((r) => !openRecords.some((o) => o.id === r.id)),
      ...openRecords.map((r) => ({ ...r, end_time: startTime })),
    ].sort(sortRuns));
    closeChipDialog();
    load();
    return created;
  };

  const handleChipBhaChange = async (value) => {
    if (!sheetId || !Number.isFinite(Number(value))) return;
    const updated = await base44.entities.Sheet.update(sheetId, { bha_length: Number(value) });
    setSheet(updated);
  };

  const handleAddChipRod = async (data) => {
    const startTime = data.start_time || new Date().toTimeString().slice(0, 5);
    const openRecords = runs.filter((r) => !r.end_time);
    if (openRecords.length) {
      await base44.entities.DrillRun.bulkUpdate(
        openRecords.map((r) => ({ id: r.id, end_time: startTime }))
      );
    }

    const chipCount = runs.filter((r) => r.record_type === "chip_section").length;
    const payload = {
      ...data,
      start_time: startTime,
      end_time: data.end_time || null,
      record_type: "chip_section",
      sheet_id: sheetId,
      hole_id: sheet?.name || "",
      rod_number: data.rod_number ?? (latestChip?.rod_not_completed ? latestChip.rod_number : chipCount + 1),
      date: new Date().toISOString().split("T")[0],
      run_number: nextRunNumber + chipCount,
    };
    const created = await base44.entities.DrillRun.create(payload);
    await load();
    return created;
  };

  const handleEdit = (run) => {
    openRunDialog(run);
  };

  const handleDelete = async (id) => {
    const prev = runs;
    const run = runs.find((r) => r.id === id);
    if (run) backupDeletedRun(run);
    setRuns((p) => p.filter((r) => r.id !== id));
    setDeleteTarget(null);
    try {
      await base44.entities.DrillRun.delete(id);
    } catch (e) {
      setRuns(prev);
      setDeleteTarget(null);
      throw e;
    }
    setDeletedRuns(listDeletedRuns().filter((e) => e.run?.sheet_id === sheetId));
    load();
  };

  const handleDeleteHole = async () => {
    try {
      backupDeletedHole(sheet, runs);
      await base44.entities.DrillRun.deleteMany({ sheet_id: sheetId });
      await base44.entities.Sheet.delete(sheetId);
      setDeleteTarget(null);
      navigate("/dashboard");
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveHole = async (e) => {
    e.preventDefault();
    const trimmed = holeNameDraft.trim();
    if (!trimmed) return;
    const updated = await base44.entities.Sheet.update(sheetId, { name: trimmed });
    setSheet(updated);
    setEditingHole(false);
  };

  const handleStatusChange = async (value) => {
    if (value === "Active" && sheet?.status === "Completed" && !isAtLeast(effectiveRole, "supervisor")) return;
    const prev = sheet;
    setSheet({ ...sheet, status: value });
    try {
      const updated = await base44.entities.Sheet.update(sheetId, { status: value, ...(value === "Completed" ? { completed_at: new Date().toISOString() } : {}) });
      setSheet(updated);
    } catch (e) {
      setSheet(prev);
    }
  };

  // When collar values change, adjust all runs' stick up by the delta
  // (instead of changing the total depth).
  const adjustStickUpForCollarDelta = async (payload) => {
    const oldCollarSum = (sheet?.collars || []).reduce((s, c) => s + (Number(c) || 0), 0);
    const newCollarSum = (payload.collars || []).reduce((s, c) => s + (Number(c) || 0), 0);
    const delta = +((+newCollarSum) - (+oldCollarSum)).toFixed(2);
    if (delta === 0) return;
    const collarNote = `${Math.abs(delta)}m collar ${delta > 0 ? "added" : "removed"}`;
    const updates = runs
      .filter((r) => r.stick_up != null || r.end_stick_up != null)
      .map((r) => ({
        id: r.id,
        stick_up: r.stick_up != null ? +((+r.stick_up) + delta).toFixed(2) : r.stick_up,
        end_stick_up: r.end_stick_up != null ? +((+r.end_stick_up) + delta).toFixed(2) : r.end_stick_up,
        remarks: r.remarks ? `${r.remarks}\n${collarNote}` : collarNote,
      }));
    if (updates.length) {
      await base44.entities.DrillRun.bulkUpdate(updates);
      load();
    }
  };

  const handleEditHoleSave = async (payload) => {
    setSavingHole(true);
    try {
      const updated = await base44.entities.Sheet.update(sheetId, payload);
      setSheet(updated);
      await adjustStickUpForCollarDelta(payload);
      setEditHoleOpen(false);
    } finally {
      setSavingHole(false);
    }
  };

  const handleEditHoleAutoSave = async (payload) => {
    try {
      const updated = await base44.entities.Sheet.update(sheetId, payload);
      setSheet(updated);
      await adjustStickUpForCollarDelta(payload);
    } catch (e) {
      console.error(e);
    }
  };

  const handleRunAutoSave = async (payload) => {
    if (!editingRun) return;
    try {
      await base44.entities.DrillRun.update(editingRun.id, payload);
      setRuns((p) => p.map((r) => (r.id === editingRun.id ? { ...r, ...payload } : r)));
    } catch (e) {
      console.error(e);
    }
  };

  const handleExport = async (type) => {
    setExporting(true);
    try {
      if (type === "csv") await exportRunsToCSV(runs, undefined, sheet?.hole_type);
      else if (type === "pdf") await exportRunsToPDF([...runs].reverse(), undefined, sheet?.hole_type);
    } finally {
      setExporting(false);
    }
  };

  const handleAddAlert = async (data) => {
    try {
      await base44.entities.DepthAlert.create({
        sheet_id: sheetId,
        depth: data.depth,
        description: data.description,
      });
      setAlertDialogOpen(false);
      loadAlerts();
    } catch (e) {
      console.error(e);
    }
  };

  const handleEditAlert = async (id, data) => {
    try {
      await base44.entities.DepthAlert.update(id, data);
      loadAlerts();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteAlert = async (id) => {
    try {
      await base44.entities.DepthAlert.delete(id);
      loadAlerts();
    } catch (e) {
      console.error(e);
    }
  };

  // Active alerts: all upcoming alerts within 18 m of current depth.
  const activeAlerts = useMemo(() => {
    // Compare displayed/calculated depths at the same 2-decimal precision used
    // by the drilling UI. An alert is passed only when current depth is strictly
    // greater than the alert depth. At equality it remains active as 0.00 m until.
    const d = Number(currentDepth);
    const current = Number.isFinite(d) ? Number(d.toFixed(2)) : 0;
    return (alerts || [])
      .map((a) => {
        const rawTarget = Number(a.depth);
        const target = Number.isFinite(rawTarget) ? Number(rawTarget.toFixed(2)) : 0;
        return { ...a, _diff: Number((target - current).toFixed(2)) };
      })
      .filter((a) => a._diff >= 0 && a._diff <= 18)
      .sort((a, b) => a._diff - b._diff);
  }, [alerts, currentDepth]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <AppHeader
        title={sheet?.name || "Run Sheet"}
        subtitle={sheet?.description || "Drilling operations log"}
        backTo="/dashboard"
        action={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setEditHoleOpen(true)}
              className="h-11 w-11"
              title="Edit hole details"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDeleteTarget({ type: "hole" })}
              className="text-red-500 hover:text-red-600 h-11 w-11"
              title="Delete hole"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        }
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 pb-24">
        <PullToRefresh onRefresh={load}>
          {/* Hole ID editor */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Hole ID:</span>
            {editingHole ? (
              <form onSubmit={handleSaveHole} className="flex items-center gap-2">
                <Input
                  value={holeNameDraft}
                  onChange={(e) => setHoleNameDraft(e.target.value)}
                  className="max-w-[240px] rounded-xl"
                  autoFocus
                />
                <Button type="submit" size="icon" className="bg-slate-900 hover:bg-slate-800 h-11 w-11 rounded-xl">
                  <Check className="w-4 h-4" />
                </Button>
                <Button type="button" variant="ghost" size="icon" onClick={() => setEditingHole(false)} className="h-11 w-11">
                  <X className="w-4 h-4" />
                </Button>
              </form>
            ) : (
              <div className="flex items-center gap-1">
                <span className="text-lg font-bold text-slate-900 dark:text-slate-100">{sheet?.name}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => { setHoleNameDraft(sheet?.name || ""); setEditingHole(true); }}
                  className="h-11 w-11 text-slate-500 dark:text-slate-400"
                  title="Edit Hole ID"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              </div>
            )}
            <DropdownMenu open={newMenuOpen} onOpenChange={setNewMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800 gap-1.5 rounded-xl h-10 px-3">
                  New <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="rounded-xl">
                {sheet?.hole_type !== "Chipping" && conventionalMethod !== "Chip" && (
                  <DropdownMenuItem onClick={handleNewRun} className="gap-2">
                    <Plus className="w-4 h-4" /> New Run
                  </DropdownMenuItem>
                )}
                {(sheet?.hole_type === "Chipping" || (sheet?.hole_type === "Conventional" && conventionalMethod === "Chip")) && (
                  <DropdownMenuItem
                    onClick={() => { setNewMenuOpen(false); openChipDialog(null); }}
                    className="gap-2 text-amber-700 dark:text-amber-300"
                  >
                    <Disc3 className="w-4 h-4" /> Add Chip Section
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => { setNewMenuOpen(false); setAlertDialogOpen(true); }}
                  className="gap-2 text-amber-700 dark:text-amber-300"
                >
                  <Bell className="w-4 h-4" /> Prediction
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {sheet?.hole_type === "Conventional" && (
              <Button
                variant="outline"
                onClick={handleChangeMethod}
                className="rounded-xl h-10 px-3"
              >
                Change to {conventionalMethod === "Chip" ? "Core" : "Chip"}
              </Button>
            )}
          </div>

          {/* Status badge + editor */}
          {sheet && (
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Status:</span>
              <StatusBadge status={sheet.status} />
              <div className="w-40">
                <MobileSelect
                  value={sheet.status || "Active"}
                  onValueChange={handleStatusChange}
                  options={STATUS_OPTIONS}
                  placeholder="Select status"
                  disabled={sheet.status === "Completed" && !isAtLeast(effectiveRole, "supervisor")}
                />
              </div>
            </div>
          )}

          {/* Status editor + Hole dimensions */}
          {(sheet?.barrel_length != null || sheet?.table_height != null || sheet?.barrel_table_height != null || sheet?.status) && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-500 mb-4 dark:text-slate-400">
              {sheet?.barrel_length != null && (
                <span>Barrel Length: <span className="font-medium text-slate-600 dark:text-slate-300">{sheet.barrel_length} m</span></span>
              )}
              {sheet?.table_height != null && (
                <span>Table Height: <span className="font-medium text-slate-600 dark:text-slate-300">{sheet.table_height} m</span></span>
              )}
              {sheet?.barrel_table_height != null && (
                <span>Barrel-Table Height: <span className="font-medium text-slate-600 dark:text-slate-300">{sheet.barrel_table_height} m</span></span>
              )}
              {sheet?.run_size != null && (
                <span>Rod Length: <span className="font-medium text-slate-600 dark:text-slate-300">{sheet.run_size} m</span></span>
              )}
              {currentDepth > 0 && (
                <span>Current Depth: <span className="font-medium text-slate-600 dark:text-slate-300">{currentDepth} m</span></span>
              )}
              {latestTotalRodCount != null && (
                <span>Total Rod Count: <span className="font-medium text-slate-600 dark:text-slate-300">{latestTotalRodCount}</span></span>
              )}
              {sheet?.bha_length != null && (
                <span>BHA Length: <span className="font-medium text-slate-600 dark:text-slate-300">{sheet.bha_length} m</span></span>
              )}
              {sheet?.casing_installed_depth != null && (
                <span>Casing Depth: <span className="font-medium text-slate-600 dark:text-slate-300">{sheet.casing_installed_depth} m</span></span>
              )}
              {(sheet?.collars || []).map((c, i) => (
                <span key={i}>Collar {i + 1}: <span className="font-medium text-slate-600 dark:text-slate-300">{c} m</span></span>
              ))}
              {deepestCompleted?.rods_in_hole != null && (
                <span>Rods in Hole: <span className="font-medium text-slate-600 dark:text-slate-300">{deepestCompleted.rods_in_hole}</span></span>
              )}
            </div>
          )}

          {rodRequirement && sheet?.hole_type === "Conventional" && (
            <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400">{rodRequirement.method} rod requirement</div>
                  <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {rodRequirement.rods} {rodRequirement.rods === 1 ? "rod" : "rods"} required to touch bottom
                  </div>
                </div>
                {rodRequirement.stickUp > 0.001 && (
                  <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                    {rodRequirement.stickUp.toFixed(2)} m stickup
                  </div>
                )}
              </div>
              <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                {rodRequirement.method === "Chip" ? "BHA − table height" : "Barrel − table height"} = {rodRequirement.offset.toFixed(2)} m · Rod length = {rodRequirement.rodLength.toFixed(2)} m
              </div>
            </div>
          )}

          {/* Export / actions */}
          <div className="flex flex-wrap justify-end gap-2 mb-4">
            <div className="relative">
              <Button variant="outline" onClick={() => setExportMenuOpen(v=>!v)} disabled={exporting || runs.length === 0} className="rounded-xl gap-2 bg-white dark:bg-slate-900 dark:border-slate-800 dark:text-slate-100"><Download className="w-4 h-4" /> Export <ChevronDown className="w-4 h-4" /></Button>
              {exportMenuOpen && <div className="absolute right-0 mt-2 z-20 w-36 rounded-xl border bg-white p-1 shadow-lg dark:bg-slate-900 dark:border-slate-700">
                <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800" onClick={()=>{setExportMenuOpen(false);handleExport("csv")}}><FileSpreadsheet className="inline w-4 h-4 mr-2"/>CSV</button>
                <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800" onClick={()=>{setExportMenuOpen(false);handleExport("pdf")}}><FileText className="inline w-4 h-4 mr-2"/>PDF</button>
              </div>}
            </div>
            <Button variant="outline" onClick={()=>setSummaryOpen(true)} className="rounded-xl gap-2"><FileText className="w-4 h-4"/> Summary</Button>
            <Button
              variant="outline"
              onClick={() => setAlertsListOpen(true)}
              className="rounded-xl gap-2 bg-white dark:bg-slate-900 dark:border-slate-800 dark:text-slate-100"
            >
              <Bell className="w-4 h-4" /> Alerts
            </Button>
          </div>

          {/* Restore recently deleted run on this sheet */}
          {deletedRuns[0] && (
            <div className="mb-4">
              <DeletedRunRestoreBar
                entry={deletedRuns[0]}
                onRestored={() => {
                  removeDeletedRun(deletedRuns[0].id);
                  setDeletedRuns(listDeletedRuns().filter((e) => e.run?.sheet_id === sheetId));
                  load();
                }}
                onDismiss={(id) => {
                  removeDeletedRun(id);
                  setDeletedRuns(listDeletedRuns().filter((e) => e.run?.sheet_id === sheetId));
                }}
              />
            </div>
          )}

          {/* Predictions — approaching user-defined target depths */}
          {activeAlerts.length > 0 && (
            <div className="mb-4 rounded-xl border border-red-400 bg-red-50 px-3 py-2 dark:border-red-700 dark:bg-red-950/50">
              <div className="flex items-start gap-2 text-red-700 dark:text-red-300">
                <AlertOctagon className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <div className="flex-1 space-y-0.5">
                  {activeAlerts.map((a) => (
                    <div key={a.id} className="text-xs font-medium">
                      {a._diff.toFixed(2)} m until {a.description || "target depth"}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Missing data alert — gap between consecutive records */}
          {gaps.length > 0 && (
            <div className="mb-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 dark:border-red-900 dark:bg-red-950/40">
              <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span className="font-semibold text-sm">Missing data</span>
              </div>
              <ul className="mt-1.5 space-y-1 text-xs text-red-600 dark:text-red-400">
                {gaps.map((g, i) => (
                  <li key={i}>
                    Gap between <span className="font-medium">{g.prevLabel}</span> (ends {g.expected} m)
                    {" "}and <span className="font-medium">{g.currLabel}</span> (starts {g.actual} m)
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* List */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin dark:border-slate-800 dark:border-t-slate-300" />
            </div>
          ) : runs.length === 0 ? (
            <div className="text-center py-20">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3 dark:text-slate-700" />
              <p className="text-slate-500 font-medium dark:text-slate-400">No drill runs yet</p>
              <p className="text-slate-400 text-sm mt-1 dark:text-slate-500">
                {(sheet?.hole_type === "Chipping" || (sheet?.hole_type === "Conventional" && conventionalMethod === "Chip"))
                  ? 'Click “Add Chip Section” to log your first entry.'
                  : 'Click “New Run” to log your first entry.'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {runs.map((run, i) => {
                const currIP = isRunInProgress(run);
                const prevIP = i > 0 && isRunInProgress(runs[i - 1]);
                let heading = null;
                if (i === 0) heading = currIP ? "In Progress" : "Completed";
                else if (currIP !== prevIP) heading = currIP ? "In Progress" : "Completed";
                const showGapBefore = i > 0 && gapBeforeIds.has(run.id);
                return (
                  <React.Fragment key={run.id}>
                    {heading && (
                      <div className="pt-4 pb-1">
                        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{heading}</h2>
                      </div>
                    )}
                    {showGapBefore && (
                      <div className="flex items-center gap-2 px-2">
                        <div className="h-1 w-1 rounded-full bg-red-500" />
                        <div className="h-[2px] flex-1 rounded-full bg-red-500/60" />
                        <span className="text-[10px] font-medium text-red-500 whitespace-nowrap">
                          missing data
                        </span>
                      </div>
                    )}
                    {run.record_type === "chip_section" ? (
                      <ChipSectionRow
                        run={run}
                        bhaLength={sheet?.bha_length}
                        onEdit={() => openChipDialog(run)}
                        onDelete={() => setDeleteTarget({ type: "run", id: run.id })}
                        holeType={sheet?.hole_type}
                      />
                    ) : (
                      <DrillRunRow
                        run={run}
                        displayNumber={runNumberMap[run.id]}
                        onEdit={() => handleEdit(run)}
                        onDelete={() => setDeleteTarget({ type: "run", id: run.id })}
                        holeType={sheet?.hole_type}
                        rodLength={sheet?.run_size}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}

          <div className="pt-4"><Button variant="outline" onClick={()=>{ if (!currentDepth) return; const current = runs.find(r=>isRunInProgress(r)); if (current && current.to_depth == null) { setCasingWarning({ depth: currentDepth, blocked:true }); return; } setCasingWarning({depth: currentDepth, pending:true}); }} className="rounded-xl gap-2"><PackageOpen className="w-4 h-4"/> Install Casing</Button></div>

          {/* Signatures */}
          {sheet && <SignatureSection sheet={sheet} />}
        </PullToRefresh>
      </main>

      <Dialog open={runDialogOpen} onOpenChange={(open) => { if (!open) closeRunDialog(); }}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-2xl max-h-[calc(100dvh-1rem)] overflow-y-auto rounded-2xl pb-[calc(1rem+env(safe-area-inset-bottom))] sm:w-full sm:max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {editingRun ? "Edit Drill Run" : "New Drill Run"}
            </DialogTitle>
          </DialogHeader>
          <DrillRunForm
            onSubmit={handleSubmit}
            onAutoSave={handleRunAutoSave}
            sheetId={sheetId}
            initialData={editingRun}
            defaultRunNumber={nextRunNumber}
            defaultFromDepth={deepestCompleted ? (getEffectiveRunToDepth(deepestCompleted, sheet?.hole_type, sheet?.run_size) ?? deepestCompleted.to_depth) : undefined}
            defaultStartStickUp={previousRunEndStickUp}
            barrelTableHeight={sheet?.barrel_table_height}
            runSize={sheet?.run_size}
            defaultDriller={sheet?.drillers?.length ? sheet.drillers[sheet.drillers.length - 1] : undefined}
            holeType={sheet?.hole_type}
            conventionalMethod={conventionalMethod}
            currentDepth={currentDepth}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={chipDialogOpen} onOpenChange={(open) => { if (!open) closeChipDialog(); }}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-2xl max-h-[calc(100dvh-1rem)] overflow-y-auto rounded-2xl pb-[calc(1rem+env(safe-area-inset-bottom))] sm:w-full sm:max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {editingChip ? "Edit Chip Section" : "New Chip Section"}
            </DialogTitle>
          </DialogHeader>
          <ChipSectionForm
            onSubmit={handleChipSubmit}
            initialData={editingChip}
            defaultFromDepth={latestCoreTotalDepth ?? latestChipDepth ?? currentDepth}
            rodLength={sheet?.run_size ?? 6}
            bhaLength={sheet?.bha_length ?? 0}
            tableHeight={sheet?.chip_table_height ?? sheet?.table_height ?? 0}
            onBhaChange={handleChipBhaChange}
            onAddRod={handleAddChipRod}
            defaultRodNumber={nextChipRodNumber}
            finishIncompleteRod={nextChipFinishesIncomplete}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={editHoleOpen} onOpenChange={setEditHoleOpen}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-2xl max-h-[calc(100dvh-1rem)] overflow-y-auto rounded-2xl pb-[calc(1rem+env(safe-area-inset-bottom))] sm:w-full sm:max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Edit Hole</DialogTitle>
          </DialogHeader>
          {sheet && (
            <HoleForm
              initialData={sheet}
              onSubmit={handleEditHoleSave}
              onAutoSave={handleEditHoleAutoSave}
              submitting={savingHole}
              submitLabel="Save"
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={summaryOpen} onOpenChange={setSummaryOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle>Run Sheet Summary</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-slate-500">Hole</span><div className="font-semibold">{sheet?.name || "—"}</div></div>
            <div><span className="text-slate-500">Status</span><div className="font-semibold">{sheet?.status || "—"}</div></div>
            {sheet?.barrel_length != null && <div><span className="text-slate-500">Barrel Length</span><div className="font-semibold">{sheet.barrel_length} m</div></div>}
            {sheet?.table_height != null && <div><span className="text-slate-500">Table Height</span><div className="font-semibold">{sheet.table_height} m</div></div>}
            {sheet?.barrel_table_height != null && <div><span className="text-slate-500">Barrel-Table Height</span><div className="font-semibold">{sheet.barrel_table_height} m</div></div>}
            {sheet?.run_size != null && <div><span className="text-slate-500">Rod Length</span><div className="font-semibold">{sheet.run_size} m</div></div>}
            <div><span className="text-slate-500">Current Depth</span><div className="font-semibold">{currentDepth} m</div></div>
            {sheet?.bha_length != null && <div><span className="text-slate-500">BHA Length</span><div className="font-semibold">{sheet.bha_length} m</div></div>}
            {sheet?.casing_installed_depth != null && <div><span className="text-slate-500">Casing Depth</span><div className="font-semibold">{sheet.casing_installed_depth} m</div></div>}
            {(sheet?.collars || []).map((c, i) => <div key={`summary-collar-${i}`}><span className="text-slate-500">Collar {i + 1}</span><div className="font-semibold">{c} m</div></div>)}
            <div><span className="text-slate-500">Rods in Hole</span><div className="font-semibold">{deepestCompleted?.rods_in_hole ?? runs.filter(r => r.rods_in_hole != null).sort((a,b)=>(getEffectiveRunToDepth(b, sheet?.hole_type, sheet?.run_size)||0)-(getEffectiveRunToDepth(a, sheet?.hole_type, sheet?.run_size)||0))[0]?.rods_in_hole ?? "—"}</div></div>
            <div><span className="text-slate-500">Total Rod Count</span><div className="font-semibold">{latestTotalRodCount ?? "—"}</div></div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!casingWarning} onOpenChange={(open)=>!open&&setCasingWarning(null)}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader><DialogTitle>Install Casing</DialogTitle></DialogHeader>
          {casingWarning?.blocked ? <p className="text-sm text-amber-600">Install casing at {Number(casingWarning?.depth || 0).toFixed(2)} m. The current run must have an end depth before casing can be installed.</p> : <p className="text-sm">Install casing at {Number(casingWarning?.depth || 0).toFixed(2)} m?</p>}
          <div className="flex justify-end gap-2"><Button variant="outline" className="rounded-xl" onClick={()=>setCasingWarning(null)}>No</Button><Button className="rounded-xl" disabled={casingWarning?.blocked || casingWarning?.depth == null} onClick={async()=>{const depth=Number(casingWarning.depth); if (!Number.isFinite(depth)||depth<=0) return; const updated=await base44.entities.Sheet.update(sheetId,{casing_installed_depth:depth});setSheet(updated);setCasingWarning(null)}}>Yes</Button></div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget?.type === "hole"
                ? "Delete this hole?"
                : "Delete this run?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === "hole"
                ? "This deletes the hole and all of its runs. You can restore it from the Dashboard's Recently deleted section."
                : "This deletes the run. You can restore it from the banner at the top of this sheet."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget?.type === "hole") handleDeleteHole();
                else if (deleteTarget?.id) handleDelete(deleteTarget.id);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
<DepthAlertSplash
        open={alertDialogOpen}
        onOpenChange={setAlertDialogOpen}
        onSubmit={handleAddAlert}
        currentDepth={currentDepth}
      />

      <AlertsListDialog
        open={alertsListOpen}
        onOpenChange={setAlertsListOpen}
        alerts={alerts}
        currentDepth={currentDepth}
        onAdd={handleAddAlert}
        onEdit={handleEditAlert}
        onDelete={handleDeleteAlert}
      />
</div>
  );
}